import React, { useEffect, useState } from "react";
import TransactionModal from "./TransactionModal";
import CreditFilterDropdown from "./CreditFilterDropdown";
import CreditPurchaseModal from "./CreditPurchaseModal";
import {
  fetchAutoPurchase,
  fetchUserCreditBalance,
} from "../../apis/creditService"; // Import the service
import {
  getUserCreditHistory,
  getCreditHistoryDetails,
} from "../../apis/creditService";
import { CreditEntry, CreditEntryResponse, CreditHistoryDetail } from "./types";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import CreditHistory from "../../assets/images/CreditHistory.svg";

interface CreditFilterParams {
  fromDate?: string;
  toDate?: string;
  isCreditSpent?: boolean;
  isCreditReceived?: boolean;
}

type SortOrder = string;

export interface CreditBalanceResponse {
  availableCredits: number;
  isAutoPurchase: boolean;
}

const Index = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [sortOrder, setSortOrder] = useState<SortOrder>("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // Adjust as needed

  const [creditHistory, setCreditHistory] = useState<CreditEntryResponse>({
    items: [],
    totalCount: 0,
    page: currentPage,
    pageSize: pageSize,
  });

  const [creditHistoryData, setCreditHistoryData] = useState<
    CreditEntry[] | null
  >();
  const [creditBalance, setCreditBalance] = useState<CreditBalanceResponse>();
  const [selectedTransaction, setSelectedTransaction] =
    useState<CreditHistoryDetail | null>(null);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null); // State to handle errors
  const [loading, setLoading] = useState<boolean>(true);
  const randomBg = ["#2892C5", "#58BE74", "#F1A560", "#90A9FF", "#EF8B87"];
  const creditTypeColors: { [key: string]: string } = {
    Respond: "#58BE74", // Light Sky Blue
    Request: "#F1A560", // Light Amber
    Purchase: "#2892C5", // Soft Mint Green
    Signup: "#90A9FF", // Pale Indigo
    Referral: "#EF8B87", // Light Pink
  };
  const [isFiltered, setIsFiltered] = useState(false);

  const [filters, setFilters] = useState<CreditFilterParams>({
    fromDate: "",
    toDate: "",
    isCreditSpent: undefined,
    isCreditReceived: undefined,
  });

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(creditHistory?.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const totalPages = Math.ceil(creditHistory?.totalCount / pageSize);

    // If 5 or fewer pages, show all pages
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always include current page, and try to have 2 pages before and after
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    // Adjust if we're at the end
    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - 4);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const fetchData = async () => {
    showLoader();
    try {
      const startTime = Date.now();
      const balanceResponse = await fetchUserCreditBalance();
      const historyResponse = await getUserCreditHistory({
        page: currentPage,
        size: pageSize,
        sortBy: "CreatedDate",
        sortOrder: "asc",
      });
      let autoPurchaseResult = "false";
      if (balanceResponse.isAutoPurchase) {
        autoPurchaseResult = await fetchAutoPurchase();
      }
      const elapsedTime = Date.now() - startTime;
      const delay = Math.max(500, 1000 - elapsedTime);

      setTimeout(() => {
        setCreditBalance({
          availableCredits: balanceResponse.availableCredits,
          isAutoPurchase: balanceResponse.isAutoPurchase,
        });
        setCreditHistory(historyResponse);
        setCreditHistoryData(historyResponse?.items);
        console.log("data", historyResponse);
        setLoading(false);
        hideLoader();
      }, delay);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setLoading(false);
      hideLoader();
    }
  };

  // ✅ Call fetchData on initial load
  // useEffect(() => {
  //   fetchData();
  // }, [currentPage]);

  useEffect(() => {
    if (!isFiltered) {
      fetchData();
    } else {
      // When filtered, fetch data with filters for the current page
      (async () => {
        try {
          showLoader();
          const filteredHistory = await getUserCreditHistory({
            page: currentPage,
            size: pageSize,
            sortBy: "CreatedDate",
            sortOrder: "asc",
            ...filters,
          });
          setCreditHistory(filteredHistory);
          setCreditHistoryData(filteredHistory?.items);
        } catch (error) {
          console.error("Failed to fetch paginated filtered data:", error);
        } finally {
          hideLoader();
        }
      })();
    }
  }, [currentPage]);

  const handleViewDetails = async (id: number) => {
    try {
      console.log(id);
      // Make the API call to fetch credit history details using the ID
      const transactionDetails = await getCreditHistoryDetails(id);

      // Set the fetched data to selectedTransaction
      setSelectedTransaction(transactionDetails);
      console.log(transactionDetails);

      // Open the modal to display the transaction details
      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch credit history details:", error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false); // Close dropdown
  };

  const handleFilterApply = async (filters: CreditFilterParams) => {
    try {
      showLoader();

      setIsFiltered(true); // ✅ mark as filtered
      setCurrentPage(1); // reset to page 1 on new filter

      // Save filters in parent state
      setFilters({
        fromDate: filters.fromDate || "",
        toDate: filters.toDate || "",
        isCreditSpent: filters.isCreditSpent,
        isCreditReceived: filters.isCreditReceived,
      });

      const filteredHistory = await getUserCreditHistory({
        page: 1,
        size: 10,
        sortBy: "CreatedDate",
        sortOrder: "asc",
        ...filters,
      });

      setCreditHistory(filteredHistory);
      setCreditHistoryData(filteredHistory?.items);
    } catch (error) {
      console.error("Failed to fetch filtered credit history:", error);
    } finally {
      hideLoader();
    }
  };

  const handleClearAll = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      isCreditSpent: undefined,
      isCreditReceived: undefined,
    });
    setIsFiltered(false); // ✅ back to default mode
    setCurrentPage(1);
    // Optionally reload default data
    fetchData();
  };

  return (
    <div className="">
      <section className="flex justify-between p-8 h-[125.5px] bg-cover ">
        <p className="text-black text-lg font-semibold">Tokens</p>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <i className="st-home text-black text-xs" aria-hidden="true"></i>
            <a className="text-black text-sm font-extralight" href="/search">
              Home
            </a>
            <i className="st-arrow-right text-black text-xs"></i>
          </div>
          <p className="text-black text-sm font-bold">Token</p>
        </div>
      </section>
      <div className="relative   mx-5">
        <section className="absolute w-full  -top-[72px] flex flex-wrap items-center gap-6 justify-between rounded-lg mt-6 p-5 bg-white">
          <div className="flex items-center gap-4 ">
            <img className="w-16 h-16" src={CreditHistory} alt="" />
            <div>
              <p className="text-sm text-lightgray-200 font-medium">
                Total Tokens Earned
              </p>
              <span className="text-grease-100 text-3xl font-bold mt-2 block">
                {(creditBalance?.availableCredits || 0)
                  .toString()
                  .padStart(2, "0")}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowCreditPurchaseModal(true)}
            data-modal-target="default-modal-2"
            data-modal-toggle="default-modal-2"
            className="py-4 px-8 rounded-lg text-[#F5F9FE] text-center shadow-button bg-[#FFBC00] text-xs"
          >
            PURCHASE TOKENS
          </button>

          <CreditPurchaseModal
            onPaymentComplete={fetchData}
            isOpen={showCreditPurchaseModal}
            onClose={() => setShowCreditPurchaseModal(false)}
          />
        </section>
      </div>
      <section className="bg-[rgb(244,244,244)] rounded-lg mt-6 p-6">
        <div className="flex flex-wrap gap-4 items-center justify-between pb-4 border-b border-lightgray-100  mt-6">
          <p className="text-grease-100 text-sm font-semibold">
            Token History
          </p>
          <div className="flex flex-wrap items-center gap-3 relative">
            {/* <div className="relative">
              <input
                className="block w-full p-3 pl-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                placeholder="Search by keyword"
              />
              <button className="absolute w-fit inset-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none">
                <i className="st-search text-lightgray-200"></i>
              </button>
            </div> */}
            {/* Filter Button */}
            <button
              id="creditFilterDropdownButton"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex gap-2 p-3 border bg-[#FFBC00] rounded-lg"
            >
              <i className="st-filter text-[#F5F9FE]"></i>
              <p className="font-medium text-[#F5F9FE] text-sm">Filters</p>
            </button>

            {/* Filter Dropdown Content */}
            {isDropdownOpen && (
              <CreditFilterDropdown
                isOpen={isDropdownOpen}
                onClose={closeDropdown}
                onApplyFilters={handleFilterApply}
                initialFromDate={filters.fromDate}
                initialToDate={filters.toDate}
                initialStatus={
                  filters.isCreditSpent
                    ? "spent"
                    : filters.isCreditReceived
                    ? "received"
                    : ""
                }
              />
            )}
          </div>
        </div>
        {/* Credit History Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 grid-cols-1 gap-6 mt-6">
          {creditHistoryData && creditHistoryData.length > 0 ? (
            creditHistoryData.map((item: CreditEntry, index: number) => {
              const typeCapitalized =
                item.creditType.charAt(0).toUpperCase() +
                item.creditType.slice(1);
              const bgColor = creditTypeColors[typeCapitalized] || "#9CA3AF"; // Default to gray if unknown

              return (
                <div
                  key={index}
                  className="p-6 flex flex-wrap gap-6 items-center justify-between bg-white bg-opacity-60 border border-indigo-50 rounded-xl"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-[55px] h-[55px] rounded-full flex items-center justify-center"
                      style={{ backgroundColor: bgColor }}
                    >
                      <p className="text-xl text-white font-semibold leading-none">
                        {item.creditAmount > 0
                          ? `+${item.creditAmount}`
                          : item.creditAmount}
                      </p>
                    </div>

                    <div>
                      <p className="text-grease-100 text-sm">
                        {item.creditType.charAt(0).toUpperCase() +
                          item.creditType.slice(1)}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 mt-2">
                        <div className="flex items-center gap-2">
                          <i className="st-calendar text-[#737C8C] text-sm"></i>
                          <p className="text-[#737C8C] text-xs">
                            {new Date(item.createdDate).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="st-time text-[#737C8C] text-sm"></i>
                          <p className="text-[#737C8C] text-xs">
                            {new Date(item.createdDate).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              }
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewDetails(item.id)}
                    data-modal-target="default-modal"
                    data-modal-toggle="default-modal"
                    className={`text-sm font-medium text-royalblue-100 bg-white px-3 py-2 rounded-md border border-gray-200 ${
                      item.creditType === "respond" ||
                      item.creditType === "request"
                        ? ""
                        : "cursor-not-allowed opacity-50"
                    }`}
                    disabled={
                      !(
                        item.creditType === "respond" ||
                        item.creditType === "request"
                      )
                    }
                  >
                    <i className="st-eye text-royalblue-100 text-xs mr-2"></i>
                    View Details
                  </button>
                </div>
              );
            })
          ) : (
            <p className="text-center text-gray-500 text-sm col-span-2">
              No credit history available.
            </p>
          )}
        </div>

        <section>
          <nav>
            <ul className="flex items-center justify-center my-6 gap-3 text-sm">
              <li>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex items-center justify-center w-8 h-8 leading-tight ${
                    currentPage === 1
                      ? "text-gray-300 border border-gray-300 cursor-not-allowed"
                      : "text-primaryblue-50 border border-primaryblue-50 cursor-pointer"
                  } rounded-md`}
                >
                  <span className="sr-only">Previous</span>
                  <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                </button>
              </li>

              {getPageNumbers().map((pageNumber) => (
                <li key={pageNumber}>
                  <button
                    onClick={() => handlePageChange(pageNumber)}
                    className={`flex items-center justify-center w-8 h-8 rounded-md ${
                      currentPage === pageNumber
                        ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                        : "text-primaryblue-50 border border-primaryblue-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                </li>
              ))}

              <li>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={
                    currentPage >=
                    Math.ceil(creditHistory?.totalCount / pageSize)
                  }
                  className={`flex items-center justify-center w-8 h-8 ${
                    currentPage >=
                    Math.ceil(creditHistory?.totalCount / pageSize)
                      ? "text-gray-300 border border-gray-300 cursor-not-allowed"
                      : "text-primaryblue-50 border border-primaryblue-50 cursor-pointer"
                  } rounded-md`}
                >
                  <span className="sr-only">Next</span>
                  <i className="st-arrow-right text-xl font-semibold"></i>
                </button>
              </li>
            </ul>
          </nav>
        </section>
      </section>

      {selectedTransaction && (
        <TransactionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          transaction={selectedTransaction}
        />
      )}
    </div>
  );
};

export default Index;
